import { useEffect, useRef, useState } from 'react';
import { SparkleIcon } from '@phosphor-icons/react';
import { Input } from '@/components/cms/ui/input';
import { Field } from '@/modules/primitives3D/editorShell';
import { FieldLabel, FieldHint, SectionTitle } from '@/components/ui/text';
import { usePublishAiAssist } from '../usePublishAiAssist';
import { ERROR_INPUT_CLASS, FIELD_ERROR_MESSAGES, projectNameError, type FieldKey } from '../validation';
import { FieldError, RequiredMark } from '../FieldMarks';
import { CoverImageTile } from '../CoverImageTile';
import { PillButton } from '../PillButton';
import { AutoGrowTextarea } from '../AutoGrowTextarea';
import { TagField } from '@/components/ui/tag-field';
import type { PublishFlowImagesApi } from '../usePublishFlowImages';

interface Props {
  projectName: string;
  projectDescription: string;
  projectTags: string[];
  coverImages: PublishFlowImagesApi;
  projectInstanceId?: string | null;
  sceneOriginId?: string | null;
  // The scene's auto-captured screenshot (scene_instance_cover_img_url). Used as
  // the cover-generation seed when the creator hasn't set a landscape cover yet.
  sceneCoverImgUrl?: string | null;
  errors: Set<FieldKey>;
  onChange: (patch: {
    projectName?: string;
    projectDescription?: string;
    projectTags?: string[];
    storyName?: string;
    sceneIntroduction?: string;
  }) => void;
}

export function ProjectInfoStep({
  projectName,
  projectDescription,
  projectTags,
  coverImages,
  projectInstanceId,
  sceneOriginId,
  sceneCoverImgUrl,
  errors,
  onChange,
}: Props) {
  const ai = usePublishAiAssist();
  const { projectPortrait, projectLandscape, sceneLandscape } = coverImages;
  // Show a set landscape cover if present, otherwise the scene's auto-captured
  // screenshot, so the creator sees the cover-generation seed before generating.
  // This is also what generation is seeded from.
  const landscapeDisplayUrl = projectLandscape.url ?? sceneLandscape.url ?? sceneCoverImgUrl ?? null;
  const landscapeCaption =
    !projectLandscape.url && (sceneLandscape.url || sceneCoverImgUrl) ? 'From your scene' : undefined;

  const runGenerateText = async () => {
    const result = await ai.generateText({ shortDescription: projectDescription, projectInstanceId });
    console.log('[ai-covers] metadata result', result);
    const patch: Parameters<typeof onChange>[0] = {};
    if (result.projectName) patch.projectName = result.projectName;
    if (result.projectDescription) patch.projectDescription = result.projectDescription;
    if (result.projectTags?.length) patch.projectTags = result.projectTags;
    if (result.sceneName) patch.storyName = result.sceneName;
    if (result.sceneIntroduction) patch.sceneIntroduction = result.sceneIntroduction;
    if (Object.keys(patch).length) onChange(patch);
  };

  const [coverSaveError, setCoverSaveError] = useState<string | null>(null);

  const runGenerateCovers = async () => {
    if (!landscapeDisplayUrl) {
      console.warn('[ai-covers] no seed image; button should be disabled', { landscapeDisplayUrl, sceneCoverImgUrl });
      return;
    }
    setCoverSaveError(null);
    console.log('[ai-covers] generating', { seedUrl: landscapeDisplayUrl, projectInstanceId });
    const result = await ai.generateImages({ seedUrl: landscapeDisplayUrl, projectInstanceId });
    console.log('[ai-covers] backend result', result);
    try {
      if (result.portraitUrl) {
        console.log('[ai-covers] saving ProjectPortrait', result.portraitUrl);
        await coverImages.saveImage('ProjectPortrait', { imageOriginalUrl: result.portraitUrl, imageCroppedUrl: result.portraitUrl });
      }
      if (result.landscapeUrl) {
        console.log('[ai-covers] saving ProjectLandscape', result.landscapeUrl);
        await coverImages.saveImage('ProjectLandscape', { imageOriginalUrl: result.landscapeUrl, imageCroppedUrl: result.landscapeUrl });
      }
    } catch (err) {
      console.error('[ai-covers] saving generated covers failed', err);
      setCoverSaveError('The covers were generated but could not be saved. Please try again.');
    }
  };

  const coverWrapRef = useRef<HTMLDivElement>(null);
  const descWrapRef = useRef<HTMLDivElement>(null);
  const [descMinH, setDescMinH] = useState(0);

  useEffect(() => {
    const compute = () => {
      const button = coverWrapRef.current?.querySelector('button');
      const c = button?.getBoundingClientRect();
      const d = descWrapRef.current?.getBoundingClientRect();
      if (!c || !d) return;
      const target = c.bottom - d.top;
      setDescMinH(Math.max(60, target));
    };
    compute();
    const observer = new ResizeObserver(compute);
    if (coverWrapRef.current) observer.observe(coverWrapRef.current);
    if (descWrapRef.current) observer.observe(descWrapRef.current);
    window.addEventListener('resize', compute);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  return (
    <div className="grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line h-full">
      <div className="space-y-4 min-w-0 pr-7">
        <SectionTitle>Details</SectionTitle>

        <Field>
          <FieldLabel mb>
            Project name
            <RequiredMark />
          </FieldLabel>
          <Input
            className={errors.has('projectName') ? ERROR_INPUT_CLASS : undefined}
            value={projectName}
            onChange={(e) => onChange({ projectName: e.target.value })}
            placeholder="My project"
          />
          {errors.has('projectName') && <FieldError>{projectNameError(projectName)}</FieldError>}
        </Field>

        <Field>
          <FieldLabel mb>
            Description
            <RequiredMark />
          </FieldLabel>
          <div ref={descWrapRef}>
            <AutoGrowTextarea
              className={errors.has('projectDescription') ? ERROR_INPUT_CLASS : undefined}
              value={projectDescription}
              onChange={(e) => onChange({ projectDescription: e.target.value })}
              placeholder="What is this project about? A one-liner works."
              minHeightPx={descMinH}
            />
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <FieldHint>
              Write a one-liner and we'll fill in the rest.
            </FieldHint>
            <PillButton
              icon={SparkleIcon}
              label={ai.isGeneratingText ? 'Generating…' : 'Auto-fill from this'}
              loading={ai.isGeneratingText}
              disabled={projectDescription.trim().length === 0}
              onClick={() => void runGenerateText()}
            />
          </div>
          {errors.has('projectDescription') && <FieldError>{FIELD_ERROR_MESSAGES.projectDescription}</FieldError>}
          {ai.textError && <FieldError>{ai.textError}</FieldError>}
        </Field>

        <Field>
          <FieldLabel mb>
            Tags
            <RequiredMark />
          </FieldLabel>
          <TagField
            value={projectTags}
            invalid={errors.has('projectTags')}
            onChange={(next) => onChange({ projectTags: next })}
          />
          {errors.has('projectTags') ? (
            <FieldError>{FIELD_ERROR_MESSAGES.projectTags}</FieldError>
          ) : (
            <FieldHint>At least one tag.</FieldHint>
          )}
        </Field>
      </div>

      <div className="space-y-4 min-w-0 pl-7">
        <SectionTitle>
          Project images
          <RequiredMark />
        </SectionTitle>

        <div ref={coverWrapRef} className="flex items-start gap-4">
          <CoverImageTile
            aspect="9/16"
            heightRem={11}
            label="Portrait (9:16)"
            category="ProjectPortrait"
            entry={projectPortrait}
            displayUrl={projectPortrait.url}
            busy={ai.isGeneratingImages}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            onSave={(payload) => coverImages.saveImage('ProjectPortrait', payload)}
            onRemove={() => coverImages.removeImage('ProjectPortrait')}
          />
          <CoverImageTile
            aspect="16/9"
            heightRem={11}
            label="Landscape (16:9)"
            category="ProjectLandscape"
            entry={projectLandscape}
            displayUrl={landscapeDisplayUrl}
            caption={landscapeCaption}
            busy={ai.isGeneratingImages}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            onSave={(payload) => coverImages.saveImage('ProjectLandscape', payload)}
            onRemove={() => coverImages.removeImage('ProjectLandscape')}
          />
        </div>

        <div className="flex items-center justify-between gap-2">
          <FieldHint>Add a portrait and landscape cover image.</FieldHint>
          <PillButton
            icon={SparkleIcon}
            label={ai.isGeneratingImages ? 'Generating…' : 'Generate covers'}
            loading={ai.isGeneratingImages}
            // Generated covers are saved against the project, so without a
            // project id the results would be silently dropped.
            disabled={!landscapeDisplayUrl || !projectInstanceId}
            onClick={() => void runGenerateCovers()}
          />
        </div>
        {errors.has('projectImages') && <FieldError>{FIELD_ERROR_MESSAGES.projectImages}</FieldError>}
        {ai.imagesError && <FieldError>{ai.imagesError}</FieldError>}
        {coverSaveError && <FieldError>{coverSaveError}</FieldError>}
      </div>
    </div>
  );
}
